if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.variantChangeUnsubscriber = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        // Limpa o subscriber de variantChange para evitar memory leak
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = null;
        }

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // Reinicializar Splide após injetar o conteúdo
            this.reinitializeSplide();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      reinitializeSplide() {
        // Aguarda o próximo frame para garantir que o DOM foi atualizado
        requestAnimationFrame(() => {
          const productMediaElement = this.modalContent.querySelector('.product_media');
          const paginationElement = this.modalContent.querySelector('.pagination');

          if (productMediaElement && window.Splide) {
            // Destrói instâncias antigas se existirem
            if (productMediaElement.splide) {
              productMediaElement.splide.destroy();
            }

            // Busca o elemento pai que contém as classes de controle de galeria
            const mediaContainer = productMediaElement.closest('[class*="gallery-active"]');

            // Determina se deve mostrar galeria baseado no tamanho da tela e nas classes do container
            const isMobile = window.innerWidth < 1024;
            const showGalleryOnCurrentDevice = isMobile
              ? mediaContainer?.classList.contains('mobile-gallery-active')
              : mediaContainer?.classList.contains('desktop-gallery-active');

            // Cria nova instância do Splide principal
            const mediaSplide = new Splide(productMediaElement, {
              start: 0,
              pagination: !showGalleryOnCurrentDevice,
              perPage: 1,
              type: 'slider',
              gap: 10,
              padding: { right: '0%' },
              arrows: true,
              focus: 'center',
              trimSpace: false,
              breakpoints: {
                1024: {
                  padding: { right: '15%' },
                  arrows: true,
                },
              },
            });

            mediaSplide.mount();

            // Dispara evento personalizado para compatibilidade
            const splideEvent = new CustomEvent('mountedSplide', {
              detail: {
                component: mediaSplide,
                showGallery: showGalleryOnCurrentDevice
              },
            });
            document.dispatchEvent(splideEvent);

            // Adiciona listener de movimento
            mediaSplide.on('move', (index) => {
              const splideEventMove = new CustomEvent('movingSplideEvent', {
                detail: { index },
              });
              document.dispatchEvent(splideEventMove);
            });

            // Adiciona listener para mudança de variante (troca de imagem)
            if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              // Limpa subscriber anterior se existir
              if (this.variantChangeUnsubscriber) {
                this.variantChangeUnsubscriber();
              }

              this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (eventData) => {
                const { variant } = eventData.data;
                if (!variant?.featured_image?.id) return;

                const slides = productMediaElement.querySelectorAll('.splide__slide[data-media-id]');
                const targetIndex = Array.from(slides).findIndex(
                  slide => slide.dataset.mediaId == variant.featured_image.id
                );

                if (targetIndex !== -1) {
                  mediaSplide.go(targetIndex);
                }
              });
            }

            // Inicializa paginação (thumbnails) se necessário
            if (paginationElement && showGalleryOnCurrentDevice) {
              // Destrói instância antiga se existir
              if (paginationElement.splide) {
                paginationElement.splide.destroy();
              }

              const pagination = new Splide(paginationElement, {
                arrows: false,
                pagination: false,
                perPage: 5,
                type: 'slider',
                gap: 10,
                mediaQuery: 'min',
                breakpoints: {
                  1024: {
                    fixedWidth: '66px',
                  },
                },
              });

              pagination.mount();

              // Conecta thumbnails com o slider principal
              const paginationPoints = paginationElement.querySelectorAll('.pagination-slide');
              paginationPoints.forEach((point, index) => {
                point.addEventListener('click', () => {
                  mediaSplide.go(index);
                });
              });

              // Sincroniza paginação com movimento do slider
              mediaSplide.on('move', (index) => {
                pagination.go(index);
              });
            }
          }
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.removeQuickAddSpecificClasses(productElement);
      }

      removeQuickAddSpecificClasses(productElement) {
        // Remove a classe lg:tw-w-1/2 da div que envelopa o produto
        const mediaWrapper = productElement.querySelector('.product-slider-media-container');
        if (mediaWrapper) {
          mediaWrapper.classList.remove('lg:tw-w-1/2');
          mediaWrapper.classList.add('tw-mx-auto');
          mediaWrapper.classList.add('tw-w-full');
          mediaWrapper.classList.add('lg:tw-w-[60%]');

          // Remove a classe lg:tw-h-[673px] de todos os slides do Splide
          const mediaSlides = mediaWrapper.querySelectorAll('.media-slide');
          mediaSlides.forEach(slide => {
            slide.classList.remove('lg:tw-h-[673px]');
          });
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
